import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Box,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Close as CloseIcon,
} from '@mui/icons-material';

import MultipartFileUpload from '../Workflows/MultipartFileUpload';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogCloseIconSx,
  harmonateFormFieldsSx,
  harmonateSelectMenuProps,
} from '../../../theme/harmonateStyles';

const BankUpload = ({ open, onClose, onUploadComplete }) => {
  const [property, setProperty] = useState('');
  const [properties, setProperties] = useState([]);

  // Fetch properties data
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const token = localStorage.getItem('token');
        const tenantId = localStorage.getItem('tenant_id');

        if (!token || !tenantId) {
          console.error('Authentication token or tenant ID not found');
          return;
        }

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}conductor/user-tenant-properties?tenant_id=${tenantId}&user_id=${localStorage.getItem('user_id')}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch properties: ${response.status}`);
        }

        const propertiesData = await response.json();
        setProperties(propertiesData);
      } catch (error) {
        console.error('Error fetching properties:', error);
      }
    };

    if (open) {
      // Reset dropdown when opening dialog
      setProperty('');
      fetchProperties();
    }
  }, [open]);

  const handleCloseDialog = () => {
    onClose();
  };

  const handleUploadComplete = (results) => {
    // Add property selection to results
    const enhancedResults = results.map(result => ({
      ...result,
      additional_details: {
        property: property
      }
    }));

    if (onUploadComplete) {
      onUploadComplete(enhancedResults);
    }

    // Close dialog after successful upload
    handleCloseDialog();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseDialog}
      fullWidth
      maxWidth="lg"
      PaperProps={harmonateDialogPaperProps}
    >
      <DialogTitle sx={harmonateDialogTitleSx}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 500, color: ht.white }}>
            Bank Upload
          </Typography>
          <IconButton aria-label="close" onClick={handleCloseDialog} sx={harmonateDialogCloseIconSx}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ ...harmonateDialogContentSx, ...harmonateFormFieldsSx }}>
        {/* Upload Options Section */}
        <Box sx={{ mb: 3, p: 2, mx: 2, bgcolor: ht.listBg, borderRadius: 1, border: `1px solid ${ht.line}` }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: ht.text }}>
            Select Upload Options
          </Typography>

          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {/* Property Dropdown */}
            <FormControl sx={{ minWidth: 200, flex: 1, mt: 1 }}>
              <InputLabel id="bank-upload-property-label" sx={{
                fontWeight: 500,
                color: ht.text,
                fontSize: '0.75rem',
                transform: 'translate(12px, -4px) scale(0.85)',
                backgroundColor: ht.listBg,
                padding: '0 2px'
              }}>
                Property
              </InputLabel>
              <Select
                labelId="bank-upload-property-label"
                value={property}
                onChange={(e) => setProperty(e.target.value)}
                displayEmpty
                MenuProps={harmonateSelectMenuProps}
                sx={{
                  bgcolor: ht.white,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: ht.line,
                  }
                }}
              >
                <MenuItem value="">
                  <em>Select Property</em>
                </MenuItem>
                {properties.map((prop) => (
                  <MenuItem key={prop.propertyId || prop.PROPERTY_ID} value={prop.propertyName || prop.PROPERTY_NAME}>
                    {prop.propertyName || prop.PROPERTY_NAME}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* File Upload Component */}
        <Box sx={{ mt: 2 }}>
          <MultipartFileUpload
            onClose={handleCloseDialog}
            onUploadComplete={handleUploadComplete}
            category="GL"
            additionalDetails={{
              property: property
            }}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default BankUpload;
